import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Clock, Plus, Trash2, Edit2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO, addMinutes } from 'date-fns';
import { downloadICalFile, getGoogleCalendarUrl } from '@/utils/calendarExport';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ScheduledStream {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  thumbnail_url: string | null;
  started_at: string;
  status: string;
}

export const StreamScheduler = () => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editingStream, setEditingStream] = useState<ScheduledStream | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: scheduledStreams, isLoading } = useQuery({
    queryKey: ['scheduled-streams'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('streams')
        .select('*')
        .eq('creator_id', user.id)
        .eq('status', 'scheduled')
        .order('started_at', { ascending: true });

      if (error) throw error;
      return data as ScheduledStream[];
    },
  });

  const handleSchedule = async () => {
    if (!title.trim() || !scheduledDate || !scheduledTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      
      if (scheduledDateTime <= new Date()) {
        toast.error('Scheduled time must be in the future');
        return;
      }

      const streamData = {
        title: title.trim(),
        description: description.trim() || null,
        category: category || null,
        status: 'scheduled' as const,
        started_at: scheduledDateTime.toISOString(),
        creator_id: user.id,
      };

      if (editingStream) {
        const { error } = await supabase
          .from('streams')
          .update(streamData)
          .eq('id', editingStream.id);

        if (error) throw error;
        toast.success('Stream schedule updated!');
      } else {
        const { error } = await supabase
          .from('streams')
          .insert([streamData]);

        if (error) throw error;
        toast.success('Stream scheduled successfully!');
      }

      // Refresh the list
      queryClient.invalidateQueries({ queryKey: ['scheduled-streams'] });
      
      // Reset form
      setOpen(false);
      setEditingStream(null);
      setTitle('');
      setDescription('');
      setCategory('');
      setScheduledDate('');
      setScheduledTime('');
    } catch (error: any) {
      console.error('Error scheduling stream:', error);
      toast.error(error.message || 'Failed to schedule stream');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (stream: ScheduledStream) => {
    setEditingStream(stream);
    setTitle(stream.title);
    setDescription(stream.description || '');
    setCategory(stream.category || '');
    
    const streamDate = parseISO(stream.started_at);
    setScheduledDate(format(streamDate, 'yyyy-MM-dd'));
    setScheduledTime(format(streamDate, 'HH:mm'));
    setOpen(true);
  };

  const handleDelete = async (streamId: string) => {
    if (!confirm('Are you sure you want to cancel this scheduled stream?')) return;

    try {
      const { error } = await supabase
        .from('streams')
        .delete()
        .eq('id', streamId);

      if (error) throw error;
      
      toast.success('Scheduled stream cancelled');
      queryClient.invalidateQueries({ queryKey: ['scheduled-streams'] });
    } catch (error: any) {
      console.error('Error deleting stream:', error);
      toast.error('Failed to cancel stream');
    }
  };

  const openNewSchedule = () => {
    setEditingStream(null);
    setTitle('');
    setDescription('');
    setCategory('');
    
    // Default to 1 hour from now
    const defaultDate = addMinutes(new Date(), 60);
    setScheduledDate(format(defaultDate, 'yyyy-MM-dd'));
    setScheduledTime(format(defaultDate, 'HH:mm'));
    setOpen(true);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Schedule Streams
              </CardTitle>
              <CardDescription>
                Plan your upcoming streams and notify your fans
              </CardDescription>
            </div>
            <Button onClick={openNewSchedule}>
              <Plus className="h-4 w-4 mr-2" />
              Schedule Stream
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center text-muted-foreground py-8">Loading...</p>
          ) : !scheduledStreams || scheduledStreams.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-20" />
              <p className="text-muted-foreground mb-4">No scheduled streams yet</p>
              <Button onClick={openNewSchedule} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Schedule Your First Stream
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {scheduledStreams.map((stream) => {
                const streamDate = parseISO(stream.started_at);
                const isUpcoming = streamDate > new Date();
                
                return (
                  <div
                    key={stream.id}
                    className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-shrink-0 p-3 bg-primary/10 rounded-lg">
                      <Clock className="h-6 w-6 text-primary" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-lg mb-1">{stream.title}</h3>
                      {stream.description && (
                        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                          {stream.description}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-3 text-sm">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Calendar className="h-3.5 w-3.5" />
                          {format(streamDate, 'MMM dd, yyyy')}
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="h-3.5 w-3.5" />
                          {format(streamDate, 'h:mm a')}
                        </div>
                        {stream.category && (
                          <span className="px-2 py-0.5 bg-secondary text-secondary-foreground rounded-full text-xs">
                            {stream.category}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost">
                            <Download className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            window.open(getGoogleCalendarUrl({
                              ...stream,
                              description: stream.description || undefined
                            }), '_blank');
                          }}>
                            Add to Google Calendar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            downloadICalFile(stream);
                          }}>
                            Download iCal File
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleEdit(stream)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDelete(stream.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingStream ? 'Edit Scheduled Stream' : 'Schedule New Stream'}
            </DialogTitle>
            <DialogDescription>
              Set up a future stream and notify your followers
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="Stream title..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={100}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="What's this stream about?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={500}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="music">Music</SelectItem>
                  <SelectItem value="comedy">Comedy</SelectItem>
                  <SelectItem value="gaming">Gaming</SelectItem>
                  <SelectItem value="sports">Sports</SelectItem>
                  <SelectItem value="talk">Talk</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="time">Time *</Label>
                <Input
                  id="time"
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                />
              </div>
            </div>

            {scheduledDate && scheduledTime && (
              <div className="bg-muted rounded-lg p-3 text-sm">
                <p className="font-medium mb-1">Scheduled for:</p>
                <p className="text-muted-foreground">
                  {format(new Date(`${scheduledDate}T${scheduledTime}`), 'EEEE, MMMM dd, yyyy \'at\' h:mm a')}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSchedule} disabled={saving}>
              {saving ? 'Saving...' : editingStream ? 'Update Schedule' : 'Schedule Stream'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
